import {LitElement, css, html, TemplateResult} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { AlertId, VesselAlert } from '../../../src/models/vessel-alerts-model';
import { VesselsModel } from '../../../src/models/vessels-model';
import { FiSubView, UiStateModel, UiStateModelParam } from '../../../src/models/ui-state-model';
import { FI_EVENT_CATEGORIES, FI_VESSEL_LIST_ACTIONS, TraceService } from '../../../src/services/trace-service';


@customElement('voyage-alert-message')
export class VoyageAlertMessage extends LitElement {
    @property(({type: Object})) alert: VesselAlert | undefined;
    @property({type: String}) message: string = "";
    @property({type: Number}) imo: number = 0;
    @property({attribute: false}) icon: TemplateResult =  SvgIcons.lightExclamationCircle;
    @property({type: Number, attribute: "display-count"}) displayCount: number = 0;

    static get styles() {
        return [
            css`
                .alert {
                    width: 100%;
                    padding: 15px;

                    display: flex;
                    align-items: center;
                    position: relative;

                    background: var(--voyage-alert-message-background-color, white);
                    border: 1px solid var(--voyage-error-red, red);
                    box-sizing: border-box;
                    border-radius: 3px;
                }

                .alert-weather {
                    width: 100%;
                    padding: 15px;

                    display: flex;
                    align-items: center;
                    position: relative;

                    background: #C5E5C2;
                    border: 1px solid #12780c;
                    box-sizing: border-box;
                    border-radius: 3px;
                }

                .alert-weather .icon {
                    color:  #141414;
                }
                .icon {
                    color: var(--voyage-error-red, red);
                    margin-right: 10px;
                }

                .icon > svg {
                    height: 18px;
                    display: block;
                    fill: currentColor;
                }

                .alert-weather .message {
                    font-family: var(--component-paragraph-ff, 'serif');
                    font-size: 14px;
                    color:  #141414;
                }

                .message {
                    font-family: var(--component-paragraph-ff, 'serif');
                    font-size: 14px;
                    color: var(--voyage-error-red, red);
                }

                .closeContainer {
                    display: flex;
                    flex-grow: 1;
                    justify-content: flex-end;
                    align-items: center;
                }
                .alert-weather .close {
                    fill: #141414;
                }

                .close {
                    padding: 0;
                    background: none;
                    border: none;
                    outline: none;
                    cursor: pointer;
                    fill: var(--voyage-error-red, red);
                }

                .close > svg {
                    width: 10px;
                    display: block;
                }

                .count {
                    background: var(--voyage-error-red, red);
                    font-family: MaerskText;
                    font-size: 12px;
                    font-weight: bold;
                    line-height: 1;
                    color: var(--voyage-alert-count-color, white);
                    text-align: center;

                    position: absolute;
                    right: 0px;
                    top: 0px;
                    transform: translate(50%, -50%);
                    border-radius: 50%;
                    width: 15px;
                    height: 15px;
                }

                .alert-weather .count {
                    background: #12780c;
                }

                .text-bold {
                    font-weight: bold;
                }

                .text-link {
                    color: var(--voyage-link-color, #0077c0);
                    font-weight: bold;
                    cursor: pointer;
                    text-decoration: underline;
                }

                .note-section {
                    font-size: 14px;
                    color: #545454;
                    margin: 5px;
                }
            `
        ];
    }

    render() {
        const className =this.alert && (this.alert.id.includes(AlertId.HeavyWeather) || this.alert.id.includes(AlertId.ParametricRoll)) ? "alert-weather" : "alert";
        this.icon = this.alert && (this.alert.id?.includes(AlertId.HeavyWeather) || this.alert.id?.includes( AlertId.ParametricRoll))? SvgIcons.lightTickIcon : SvgIcons.lightExclamationCircle;
        
        return html`
        <div class=${className}>
            <div class="icon">${this.icon}</div>
            <div class="message">${this._renderMessage(this.alert)}</div>
            <div class="closeContainer">
                <button class="close" @click=${this.close}>${SvgIcons.lightTimes}</button>
            </div>
            ${this.displayCount > 1 ? html`
            <div class="count">${this.displayCount}</div>
            ` : html``}
        </div>
        `;
    }

    close() {
        this.dispatchEvent(new Event('close'));
    }

    _renderMessage(alert?: VesselAlert) {

        if ( this.alert && (this.alert.id?.includes(AlertId.HeavyWeather) || this.alert.id?.includes(AlertId.ParametricRoll) )) {
            const alarmType = alert?.id?.includes(AlertId.HeavyWeather) ? "Heavy weather" : "Parametric roll";
            const imo = this._getImoFromAlert();
            const vesselName = VesselsModel!.getVesselName(imo);  

            // Handle notification already handles by other user
            if(!this.alert.id?.includes('error')){
                return html`<span>You have <span class="text-bold">accepted ${alarmType} alert for ${vesselName}.</span><span class="text-link" @click=${this._openVesselDetail}> View Details?</span> 
             <p class="note-section">Notes can be added to activity log section.</p></span>`;
            }

            else {
                return html`<span>This alert is already accepted by ${this.message}. <span class="text-link" @click=${this._openVesselDetail}> View Details?</span></span>`
            }
           
        }
        return html`${this.message}`;
    }

    private _getImoFromAlert() {
        return parseInt(this.alert?.id?.split("-")[2]||"0");
    }

    _openVesselDetail() {
        const imo = this._getImoFromAlert();
        UiStateModel!.setValue(UiStateModelParam.IMO, imo.toString());
        UiStateModel!.setValue(UiStateModelParam.PORT);
        UiStateModel!.setValue(UiStateModelParam.PortRegion);
        UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.VesselDetails);
        UiStateModel!.setLeftPanelVisibility(true,"hide-anchor");
        TraceService!.event(
            FI_EVENT_CATEGORIES.VESSEL_LIST,
            FI_VESSEL_LIST_ACTIONS.SELECTED_VESSEL,
            this.imo.toString()
        );
    }

};
